<script lang="ts">
	import { Icon, Progress, InlineMessage, InlineMessageType } from '@repo/commons/components'
	import { FileStatus } from '@repo/commons/models'
	import FileItem from '../FileUploadWithPreview/FileItem.svelte'
	import { fade } from 'svelte/transition'

	type ImportState = 'importing' | 'done' | 'error'

	interface Props {
		state: ImportState
		message: string
		errorMessage?: string
		progress: number
		file?: { name: string; size: number } | null
		onChooseAnother?: () => void
	}

	let { state, message, errorMessage = '', progress, file, onChooseAnother }: Props = $props()

	function formatFileSize(bytes: number): string {
		if (bytes === 0) return '0 Bytes'
		const k = 1024
		const sizes = ['Bytes', 'KB', 'MB', 'GB']
		const i = Math.floor(Math.log(bytes) / Math.log(k))
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
	}

	const fileObject = $derived(file ? new File([''], file.name, { type: 'application/json' }) : null)
</script>

<div class="import-progress-view" class:is-error-view={state === 'error'}>
	{#key state}
		{#if state === 'importing'}
			<div
				class="centered-content"
				in:fade={{ duration: 200, delay: 150 }}
				out:fade={{ duration: 150 }}
			>
				<div class="icon-circle importing">
					<Icon name="importFile" size="md" class="text-[#002C4D80]" />
				</div>
				<h3 class="heading">{message}</h3>
				<div class="progress-bar">
					<Progress value={progress} showProgress />
				</div>
			</div>
		{:else if state === 'done'}
			<div
				class="centered-content"
				in:fade={{ duration: 200, delay: 150 }}
				out:fade={{ duration: 150 }}
			>
				<div class="icon-circle done">
					<Icon name="success" size="md" variant="success" />
				</div>
				<h3 class="heading">{message}</h3>
			</div>
		{:else if state === 'error'}
			<div
				class="error-content"
				in:fade={{ duration: 200, delay: 150 }}
				out:fade={{ duration: 150 }}
			>
				<div class="icon-circle error">
					<Icon name="error" size="md" variant="danger" />
				</div>
				<h3 class="heading">{message}</h3>

				{#if fileObject && file}
					<div class="error-file">
						<FileItem
							file={fileObject}
							subtitle={formatFileSize(file.size)}
							status={FileStatus.ERROR}
							errors={[{ group: '', errors: [errorMessage] }]}
							removable={false}
							borderless
						/>
					</div>
				{/if}

				<div class="error-banner">
					<InlineMessage type={InlineMessageType.ERROR}>
						<div class="banner-content">
							<p class="banner-title">Unsupported JSON file</p>
							<p class="banner-description">{errorMessage}</p>
							{#if onChooseAnother}
								<button class="choose-another-btn" onclick={onChooseAnother}>
									<Icon name="folder" size="sm" class="text-[#002C4D80]" />
									Choose another file
								</button>
							{/if}
						</div>
					</InlineMessage>
				</div>
			</div>
		{/if}
	{/key}
</div>

<style lang="postcss">
	.import-progress-view {
		@apply relative flex items-center justify-center w-full;
		min-width: 700px;
		min-height: 200px;
	}

	.import-progress-view.is-error-view {
		min-height: auto;
	}

	.icon-circle {
		@apply flex items-center justify-center w-12 h-12 shrink-0;
		border-radius: 50%;
	}

	.icon-circle.importing {
		background-color: #00498011;
	}

	.icon-circle.done {
		background-color: #0acc920d;
	}

	.icon-circle.error {
		background-color: #e4485b0d;
	}

	.centered-content {
		@apply absolute inset-0 flex flex-col items-center justify-center py-16;
	}

	.heading {
		@apply text-xl font-semibold mt-6 mb-0;
		color: #001626e5;
	}

	.progress-bar {
		@apply w-full max-w-md mt-6;
	}

	.error-content {
		@apply flex flex-col items-center w-full py-8;
	}

	.error-file {
		@apply w-full mt-4;
	}

	.error-banner {
		@apply w-full mt-8;
	}

	.banner-content {
		@apply flex flex-col gap-1;
	}

	.banner-title {
		@apply text-sm font-semibold m-0;
		color: #001626e5;
	}

	.banner-description {
		@apply text-sm m-0;
		color: #001d33b2;
	}

	.choose-another-btn {
		@apply flex items-center gap-1.5 mt-2 text-sm font-medium cursor-pointer border-none bg-transparent p-0;
		color: #001626e5;
	}

	.choose-another-btn:hover {
		text-decoration: underline;
	}
</style>
